import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import {
  Card,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@avagency/frontend/components/ui/table';
import { EmptyState } from '@avagency/frontend/components/shell/EmptyState';
import { ErrorState } from '@avagency/frontend/components/shell/ErrorState';
import { LoadingState } from '@avagency/frontend/components/shell/LoadingState';
import { fetchPurchaseOrders, type POStatus, type POSummary } from '../api';
import { StatusBadge } from '../components/StatusBadge';
import { fmtDate, currencyFmt } from '../lib/format';

const ALL_STATUSES: POStatus[] = ['draft', 'sent', 'confirmed', 'received', 'cancelled'];

export function PurchaseOrders() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<POStatus | ''>('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['popilot', 'purchase-orders', statusFilter],
    queryFn: () =>
      fetchPurchaseOrders(statusFilter ? { status: statusFilter } : undefined),
  });

  if (isLoading) return <LoadingState rows={5} />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const orders = data?.purchase_orders ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Purchase Orders</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          All purchase orders across your connected stores. Click a row to drill in.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as POStatus | '')}
          className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="">All statuses</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
        <span className="text-xs text-muted-foreground">{orders.length} orders</span>
      </div>

      <Card>
        <CardContent className="pt-6">
          {orders.length === 0 ? (
            <EmptyState
              icon={<ClipboardList className="h-10 w-10" />}
              title={statusFilter ? `No ${statusFilter} purchase orders` : 'No purchase orders yet'}
              description={
                statusFilter
                  ? 'Clear the filter to see all orders.'
                  : 'Purchase orders will appear here once they are created.'
              }
            />
          ) : (
            <div className="rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>PO #</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right">Lines</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Expected Arrival</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((po) => (
                    <PORow
                      key={po.id}
                      po={po}
                      onClick={() => navigate(`/purchase-orders/${po.id}`)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PORow({ po, onClick }: { po: POSummary; onClick: () => void }) {
  return (
    <TableRow
      className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={onClick}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <TableCell className="font-mono text-sm">{po.po_number}</TableCell>
      <TableCell>
        <StatusBadge status={po.status} />
      </TableCell>
      <TableCell>
        {po.supplier_name ?? <span className="font-mono text-xs text-muted-foreground">{po.supplier_id}</span>}
      </TableCell>
      <TableCell className="text-right tabular-nums">{po.line_item_count}</TableCell>
      <TableCell className="text-right tabular-nums">
        {currencyFmt(po.total_cost, po.currency)}
      </TableCell>
      <TableCell>{fmtDate(po.expected_arrival)}</TableCell>
      <TableCell>{fmtDate(po.created_at)}</TableCell>
    </TableRow>
  );
}
